import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const Callback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!session) {
          throw new Error('No session found');
        }

        // Get user metadata from the session
        const { user } = session;
        const metadata = user.user_metadata;

        // Check if profile exists
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') {
          console.error('Profile fetch error:', profileError);
        }

        // If no profile exists, create one
        if (!profile) {
          const { error: createError } = await supabase
            .from('profiles')
            .insert([
              {
                id: user.id,
                display_name: metadata.full_name || metadata.user_name || user.email?.split('@')[0] || 'User',
                bio: null,
                preferred_prompt_type: null,
                plan: 'Free',
                last_login: new Date().toISOString(),
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            ]);

          if (createError) {
            console.error('Profile creation error:', createError);
            throw createError;
          }

          toast({
            title: "Welcome to PromptlyAI!",
            description: "Your account has been created successfully.",
          });
        } else {
          // Update last login time
          const { error: updateError } = await supabase
            .from('profiles')
            .update({
              last_login: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq('id', user.id);

          if (updateError) {
            console.error('Profile update error:', updateError);
          }

          toast({
            title: "Welcome back!",
            description: "You have successfully signed in.",
          });
        }

        // Redirect to dashboard
        navigate('/dashboard');
      } catch (error) {
        console.error('Auth callback error:', error);
        toast({
          title: "Authentication failed",
          description: error instanceof Error ? error.message : "Failed to complete authentication",
          variant: "destructive",
        });
        navigate('/login?error=auth');
      }
    };

    handleAuthCallback();
  }, [navigate, toast]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  );
};

export default Callback; 